import {
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  type ObservableQuery,
  type OperationVariables,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import {
  type QueryResult,
  type SuspenseQueryHookOptions,
  useApolloClient,
  useSuspenseQuery,
} from "@apollo/client/react";
import type { ResultOf, TypedDocumentNode, VariablesOf } from "@graphql-typed-document-node/core";
import type { GetServerSidePropsContext } from "next";
import { useCallback, useRef } from "react";
import { map } from "rxjs";
import { possibleTypes } from "@/b";

export function createApolloClient(
  cookies?: string,
  onResponseHeaders?: (headers: Headers) => void,
  /** Extra request headers for the server-to-server SSR call (e.g. viewer geo). */
  extraHeaders?: Record<string, string>,
) {
  let uri = "/graphql";
  const links: ApolloLink[] = [];
  if (typeof window === "undefined") {
    uri = process.env.GRAPHQL_URL ?? "http://localhost:4000/graphql";
    links.push(
      new ApolloLink((operation, forward) => {
        return forward(operation).pipe(
          map((response) => {
            const context = operation.getContext();
            const {
              response: { headers },
            } = context;
            if (headers) {
              onResponseHeaders?.(headers);
            }
            return response;
          }),
        );
      }),
    );
    links.push(
      new BatchHttpLink({
        uri,
        headers: {
          ...(cookies ? { cookie: cookies } : {}),
          ...(extraHeaders ?? {}),
        },
      }),
    );
  } else {
    links.push(
      new BatchHttpLink({
        uri,
        credentials: "include",
      }),
    );
  }

  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache({
      possibleTypes,
      typePolicies: {
        EmbeddedProductOption: {
          keyFields: false,
          fields: {
            ingredientModifications: {
              merge: (_existing, incoming) => incoming,
            },
            selections: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Employee: {
          fields: {
            roles: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        ProductSelection: {
          keyFields: false,
        },
        FieldError: {
          fields: {
            path: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Admin: {
          keyFields: () => "ADMIN",
          fields: {
            orders: {
              merge: (existing, incoming, options) => {
                if (options.args?.after === null) {
                  return incoming;
                }
                return [...existing, ...incoming];
              },
            },
            keyPerformanceIndicators: {
              merge: (_existing, incoming) => incoming,
            },
            recentGiftCards: {
              merge: (_existing, incoming) => incoming,
            },
            selectedDates: {
              merge: (_existing, incoming) => incoming,
            },
            cookDay: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Cart: {
          keyFields: () => "Cart",
          fields: {
            deliveryDays: {
              merge: (_existing, incoming) => incoming,
            },
            items: {
              merge: (_existing, incoming) => incoming,
            },
            subtotal: {
              merge: (_existing, incoming) => incoming,
            },
            messages: {
              merge: (_existing, incoming) => incoming,
            },
            issuesPreventingCheckout: {
              merge: (_existing, incoming) => incoming,
            },
            fulfillment: {
              merge: (_existing, incoming) => incoming,
            },
            fulfillmentConflicts: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Address: {
          keyFields: ["hash"],
        },
        Account: {
          fields: {
            addresses: {
              merge: (_existing, incoming) => incoming,
            },
            subscriptions: {
              merge: (_existing, incoming) => incoming,
            },
            orders: {
              merge: (_existing, incoming) => incoming,
            },
            upcomingOrders: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CartItemProduct: {
          fields: {
            discounts: {
              merge: (_existing, incoming) => incoming,
            },
            selectionPaths: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CartDeliveryDay: {
          fields: {
            reasons: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CheckoutMetadata: {
          fields: {
            availablePickupLocations: {
              merge: (_existing, incoming) => incoming,
            },
            deliveryZipCodes: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Column: {
          fields: {
            data: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        ColumnFilter: {
          fields: {
            selected: {
              merge: (_existing, incoming) => incoming,
            },
            values: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        SelectionPath: {
          fields: {
            elements: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CookDay: {
          keyFields: false,
          fields: {
            recipes: {
              merge: (_existing, incoming) => incoming,
            },
            rawIngredients: {
              merge: (_existing, incoming) => incoming,
            },
            orders: {
              merge: (_existing, incoming) => incoming,
            },
            products: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CookDayRecipe: {
          fields: {
            lastBatch: {
              merge: (_existing, incoming) => incoming,
            },
            totals: {
              merge: (_existing, incoming) => incoming,
            },
            madeInto: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CookDayProduct: {
          fields: {
            recipes: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        CookDayRecipeProduct: {
          fields: {
            selectionSummaries: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Ingredient: {
          fields: {
            subIngredients: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Invoice: {
          fields: {
            discountTotals: {
              merge: (_existing, incoming) => incoming,
            },
            discounts: {
              merge: (_existing, incoming) => incoming,
            },
            items: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        InvoiceItem: {
          fields: {
            discounts: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Metrics: {
          fields: {
            affiliateMetrics: {
              merge: (_existing, incoming) => incoming,
            },
            productMetrics: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Order: {
          fields: {
            items: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        OrderItemProduct: {
          fields: {
            ingredients: {
              merge: (_existing, incoming) => incoming,
            },
            selectionSummaries: {
              merge: (_existing, incoming) => incoming,
            },
            selections: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Product: {
          fields: {
            availability: {
              merge: (_existing, incoming) => incoming,
            },
            images: {
              merge: (_existing, incoming) => incoming,
            },
            options: {
              merge: (_existing, incoming) => incoming,
            },
            similar: {
              merge: (_existing, incoming) => incoming,
            },
            tags: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        ProductOption: {
          fields: {
            ingredientModifications: {
              merge: (_existing, incoming) => incoming,
            },
            selections: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Recipe: {
          fields: {
            ingredients: {
              merge: (_existing, incoming) => incoming,
            },
            steps: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        SharedOption: {
          fields: {
            ingredientModifications: {
              merge: (_existing, incoming) => incoming,
            },
            selections: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        SkipIssues: {
          fields: {
            issues: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Table: {
          fields: {
            columns: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Tag: {
          fields: {
            products: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        ValidationError: {
          fields: {
            errors: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
        Query: {
          fields: {
            allProducts: {
              merge: (_existing, incoming) => incoming,
            },
            allSharedOptions: {
              merge: (_existing, incoming) => incoming,
            },
            allTags: {
              merge: (_existing, incoming) => incoming,
            },
            pickupLocations: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
      },
    }),
    devtools: { enabled: true },
    ssrMode: typeof window === "undefined",
    ssrForceFetchDelay: 100,
  });
}

/**
 * CloudFront's coarse viewer-location headers (and the dev override) reach the
 * Next.js server on the page request; forward them on the SSR GraphQL call so
 * the server resolves the same default fulfillment mode it will resolve for
 * the browser's own calls — otherwise the menu selector would jump after
 * hydration. Server-to-server only; nothing here is sent to the browser.
 */
function viewerGeoHeaders(headers: GetServerSidePropsContext["req"]["headers"]) {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    if (!name.startsWith("cloudfront-viewer-") && name !== "x-vm-geo-override") continue;
    const single = Array.isArray(value) ? value[0] : value;
    if (single) out[name] = single;
  }
  return out;
}

const APOLLO_STATE_QUERY_PROP_NAME = "__APOLLO_STATE_QUERY__";

// biome-ignore lint/suspicious/noExplicitAny: TypedDocumentNode generic constraints require any
type ApolloQueryProps<Query extends TypedDocumentNode<any, any>> = {
  [APOLLO_STATE_QUERY_PROP_NAME]: {
    result: NonNullable<ObservableQuery.Result<ResultOf<Query>>["data"]>;
    query: Query;
    variables: VariablesOf<Query>;
  };
};

// biome-ignore lint/suspicious/noExplicitAny: TypedDocumentNode generic constraints require any
export async function getApolloSSRProps<Query extends TypedDocumentNode<any, any>>(
  query: Query,
  variables: VariablesOf<Query>,
  { req, res }: GetServerSidePropsContext,
): Promise<ApolloQueryProps<Query>> {
  let setCookie: string | null = null;
  const client = createApolloClient(
    req.headers.cookie,
    (headers) => {
      setCookie = headers.get("set-cookie");
      const requestId = headers.get("x-request-id");
      if (requestId) {
        res.setHeader("x-request-id", requestId);
      }
    },
    viewerGeoHeaders(req.headers),
  );

  const result = await client.query({
    query,
    variables,
  });

  if (setCookie) {
    res.setHeader("set-cookie", setCookie);
  }

  return {
    [APOLLO_STATE_QUERY_PROP_NAME]: {
      result: result.data as ResultOf<Query>,
      query,
      variables,
    },
  };
}

// biome-ignore lint/suspicious/noExplicitAny: TypedDocumentNode generic constraints require any
export function useHydratedQuery<Props extends ApolloQueryProps<TypedDocumentNode<any, any>>>(
  props: Props,
  options?: Props[typeof APOLLO_STATE_QUERY_PROP_NAME]["query"] extends TypedDocumentNode<
    infer Data,
    infer Vars extends OperationVariables
  >
    ? SuspenseQueryHookOptions<Data, Vars>
    : never,
): Props[typeof APOLLO_STATE_QUERY_PROP_NAME] extends {
  query: TypedDocumentNode<infer Data, infer Vars extends OperationVariables>;
}
  ? QueryResult<Data, Vars> & {
      data: Data;
    }
  : never {
  const isHydrated = useRef(false);
  const client = useApolloClient();
  const { query, variables: ssrVariables, result: ssrData } = props[APOLLO_STATE_QUERY_PROP_NAME];

  const hydrateCache = useCallback(() => {
    if (isHydrated.current) {
      return;
    }
    client.cache.writeQuery({
      query,
      data: ssrData,
      variables: ssrVariables,
      overwrite: true,
    });
    isHydrated.current = true;
  }, [client, query, ssrData, ssrVariables]);

  hydrateCache();

  const computedOptions = Object.assign(options ?? {}, {
    variables: options?.variables ?? ssrVariables,
  } as Partial<SuspenseQueryHookOptions<unknown, OperationVariables>>) as SuspenseQueryHookOptions<
    unknown,
    OperationVariables
  >;

  return useSuspenseQuery(
    query,
    computedOptions,
  ) as Props[typeof APOLLO_STATE_QUERY_PROP_NAME] extends {
    query: TypedDocumentNode<infer Data, infer Vars extends OperationVariables>;
  }
    ? QueryResult<Data, Vars> & {
        data: Data;
      }
    : never;
}
